import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../config/axios';
import '../styles/Dashboard.css';

function Dashboard() {
  const { user, token, signOut } = useAuth();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deletingDevice, setDeletingDevice] = useState(null);
  const [newDevice, setNewDevice] = useState({
    deviceId: '',
    name: '',
    type: 'sensor',
    metadata: {}
  });
  const [error, setError] = useState('');

  const deviceTypes = [
    { value: 'sensor', label: 'Sensor' },
    { value: 'actuator', label: 'Actuator' },
    { value: 'camera', label: 'Camera' },
    { value: 'airflow', label: 'Airflow' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchDevices();
    
    // Auto-refresh device status every 5 seconds
    const interval = setInterval(() => {
      fetchDevices();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await axios.get('/devices');
      setDevices(response.data);
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  const handleAddDevice = () => {
    setShowAddModal(true);
    setError('');
    setNewDevice({ deviceId: '', name: '', type: 'sensor', metadata: {} });
  };

  const handleSubmitDevice = async (e) => {
    e.preventDefault();
    setError('');

    if (!newDevice.deviceId || !newDevice.name) {
      setError('Device ID and Name are required');
      return;
    }

    try {
      await axios.post('/devices', {
        deviceId: newDevice.deviceId,
        name: newDevice.name,
        type: newDevice.type,
        metadata: newDevice.metadata || {}
      });
      
      setShowAddModal(false);
      fetchDevices();
      setNewDevice({ deviceId: '', name: '', type: 'sensor', metadata: {} });
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.details || 'Error adding device');
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setError('');
    setNewDevice({ deviceId: '', name: '', type: 'sensor', metadata: {} });
    setEditingDevice(null);
  };

  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setNewDevice({
      deviceId: device.deviceId,
      name: device.name,
      type: device.type,
      metadata: device.metadata || {}
    });
    setShowEditModal(true);
    setError('');
  };

  const handleUpdateDevice = async (e) => {
    e.preventDefault();
    setError('');

    if (!newDevice.name) {
      setError('Device Name is required');
      return;
    }

    try {
      await axios.put(`/devices/${editingDevice.deviceId}`, {
        name: newDevice.name,
        type: newDevice.type,
        metadata: newDevice.metadata || {}
      });
      
      setShowEditModal(false);
      fetchDevices();
      setEditingDevice(null);
      setNewDevice({ deviceId: '', name: '', type: 'sensor', metadata: {} });
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.details || 'Error updating device');
    }
  };

  const handleDeleteClick = (device) => {
    setDeletingDevice(device);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingDevice) return;

    try {
      await axios.delete(`/devices/${deletingDevice.deviceId}`);
      
      setShowDeleteConfirm(false);
      setDeletingDevice(null);
      fetchDevices();
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.details || 'Error deleting device');
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeletingDevice(null);
  };

  return (
    <div className="dashboard-container">
      <nav className="dashboard-nav">
        <h1>IoT Dashboard</h1>
        <div className="nav-actions">
          <span className="user-name">{user?.email || 'User'}</span>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </nav>

      <div className="dashboard-content">
        <div className="dashboard-header">
          <h2>My Devices</h2>
          <button onClick={handleAddDevice} className="add-device-btn">
            + Add Device
          </button>
        </div>

        {showAddModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Device</h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmitDevice} className="device-form">
              <div className="form-group">
                <label>Device ID *</label>
                <input
                  type="text"
                  value={newDevice.deviceId}
                  onChange={(e) => setNewDevice({ ...newDevice, deviceId: e.target.value })}
                  placeholder="e.g., ESP32C6Client-F0F5BD0A1C98"
                  required
                />
              </div>
              <div className="form-group">
                <label>Device Name *</label>
                <input
                  type="text"
                  value={newDevice.name}
                  onChange={(e) => setNewDevice({ ...newDevice, name: e.target.value })}
                  placeholder="e.g., Bed wind flow"
                  required
                />
              </div>
              <div className="form-group">
                <label>Device Type *</label>
                <select
                  value={newDevice.type}
                  onChange={(e) => setNewDevice({ ...newDevice, type: e.target.value })}
                  required
                >
                  {deviceTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" onClick={handleCloseModal} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Add Device
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

        {showEditModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Device</h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleUpdateDevice} className="device-form">
              <div className="form-group">
                <label>Device ID</label>
                <input
                  type="text"
                  value={newDevice.deviceId}
                  disabled
                  style={{ background: '#f5f5f5', color: '#999' }}
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>Device ID cannot be changed</small>
              </div>
              <div className="form-group">
                <label>Device Name *</label>
                <input
                  type="text"
                  value={newDevice.name}
                  onChange={(e) => setNewDevice({ ...newDevice, name: e.target.value })}
                  placeholder="e.g., Bed wind flow"
                  required
                />
              </div>
              <div className="form-group">
                <label>Device Type *</label>
                <select
                  value={newDevice.type}
                  onChange={(e) => setNewDevice({ ...newDevice, type: e.target.value })}
                  required
                >
                  {deviceTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" onClick={handleCloseModal} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Update Device
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={handleDeleteCancel}>
            <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Device</h3>
                <button className="close-btn" onClick={handleDeleteCancel}>×</button>
              </div>
              <div className="delete-content">
                <p>Are you sure you want to delete this device?</p>
                <div className="device-info-box">
                  <strong>Name:</strong> {deletingDevice?.name}<br/>
                  <strong>ID:</strong> {deletingDevice?.deviceId}<br/>
                  <strong>Type:</strong> {deletingDevice?.type}
                </div>
                <p style={{ color: '#e74c3c', fontWeight: '600' }}>This action cannot be undone.</p>
                {error && <div className="error-message">{error}</div>}
                <div className="modal-actions">
                  <button type="button" onClick={handleDeleteCancel} className="cancel-btn">
                    Cancel
                  </button>
                  <button type="button" onClick={handleDeleteConfirm} className="delete-btn">
                    Delete Device
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading devices...</div>
        ) : devices.length === 0 ? (
          <div className="no-devices">
            <p>No devices found. Add your first device!</p>
          </div>
        ) : (
          <div className="devices-grid">
            {devices.map(device => (
              <div key={device.deviceId} className="device-card">
                <div className="device-card-header">
                  <h3>{device.name}</h3>
                  <div className="device-actions">
                    <button
                      onClick={() => handleEditDevice(device)}
                      className="edit-btn"
                      title="Edit Device"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteClick(device)}
                      className="delete-btn-small"
                      title="Delete Device"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="device-id">ID: {device.deviceId}</p>
                <p className="device-type">Type: {device.type}</p>
                <p className={`device-status ${device.status}`}>
                  Connection: {device.status || 'unknown'}
                </p>
                <p className="device-line device-line-red">
                  <span className="device-line-label">RED:</span>{' '}
                  <span className={device.redState?.includes('not') ? 'line-badge off' : device.redState ? 'line-badge on' : 'line-badge na'}>
                    {device.redState || '—'}
                  </span>
                </p>
                <p className="device-line device-line-green">
                  <span className="device-line-label">Green:</span>{' '}
                  <span className={device.greenState?.includes('not') ? 'line-badge off' : device.greenState ? 'line-badge on' : 'line-badge na'}>
                    {device.greenState || '—'}
                  </span>
                </p>
                <button
                  onClick={() => navigate(`/device/${device.deviceId}`)}
                  className="control-btn"
                >
                  Control Device
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;

